import React, { useState, useEffect } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import api from '../services/api';

const Estadisticas = () => {
  const [statsCredenciales, setStatsCredenciales] = useState(null);
  const [statsAdmin, setStatsAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accesosList, setAccesosList] = useState([]);
  const [departamentosStats, setDepartamentosStats] = useState([]);

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [credencialesRes, adminRes, departamentosRes] = await Promise.all([
        api.get('/estadisticas/credenciales'),
        api.get('/estadisticas/admin'),
        api.get('/estadisticas/departamentos-completo'),
      ]);
      setStatsCredenciales(credencialesRes.data);
      setStatsAdmin(adminRes.data);
      setDepartamentosStats(departamentosRes.data.departamentos || []);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const [accesosRes] = await Promise.all([api.get('/opciones/accesos')]);
      setAccesosList(accesosRes.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  const cards = [
    {
      title: 'Total Credenciales',
      value: statsCredenciales?.total_credenciales || 0,
      gradient: 'from-green-500 to-green-600',
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    },
    {
      title: 'Credenciales Incompletas',
      value: statsCredenciales?.credenciales_incompletas || 0,
      gradient: 'from-orange-500 to-orange-600',
      icon: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    },
    {
      title: 'Logs Hoy',
      value: statsAdmin?.logs_hoy || 0,
      gradient: 'from-blue-500 to-blue-600',
      icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
    },
  ];

  // Colores para gráficos
  const COLORS = [
    '#3B82F6',
    '#10B981',
    '#8B5CF6',
    '#F59E0B',
    '#EF4444',
    '#06B6D4',
    '#EC4899',
    '#84CC16',
    '#F97316',
    '#14B8A6',
    '#6366F1',
    '#D946EF',
    '#0EA5E9',
    '#64748B',
    '#F43F5E',
  ];

  // Preparar datos para el pie chart de accesos (con nombres más cortos)
  const accesosData =
    statsCredenciales?.por_acceso
      ?.filter((item) => item.acceso && item.acceso.trim() !== '')
      .map((item) => ({
        ...item,
        acceso_corto:
          item.acceso.length > 20
            ? item.acceso.substring(0, 17) + '...'
            : item.acceso,
      })) || [];

  return (
    <div className="animate-fadeInUp">
      <h2 className="text-2xl font-bold text-white mb-6">Estadísticas</h2>

      {/* Tarjetas principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {cards.map((card, idx) => (
          <div
            key={idx}
            className={`bg-gradient-to-br ${card.gradient} rounded-2xl shadow-xl p-6 text-white transform hover:scale-[1.02] transition-all duration-300`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/80 text-sm">{card.title}</p>
                <p className="text-3xl font-bold">{card.value}</p>
              </div>
              <svg
                className="w-12 h-12 opacity-40"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d={card.icon}
                />
              </svg>
            </div>
          </div>
        ))}
      </div>

      {/* Gráficos y tablas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Departamentos (barras horizontales) */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 text-center">
            Top 5 Departamentos
          </h3>
          <div className="space-y-3">
            {statsCredenciales?.por_departamento
              ?.filter(
                (item) =>
                  item &&
                  item.departamento &&
                  item.departamento.trim() !== '' &&
                  item.cantidad > 0
              )
              .slice(0, 5)
              .map((item, idx) => (
                <div key={idx}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-orange-200">{item.departamento}</span>
                    <span className="font-medium text-white">
                      {item.cantidad}
                    </span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-orange-400 to-orange-500 h-2 rounded-full transition-all duration-500"
                      style={{
                        width: `${(item.cantidad / statsCredenciales?.total_credenciales) * 100}%`,
                      }}
                    ></div>
                  </div>
                </div>
              ))}

            {(!statsCredenciales?.por_departamento ||
              statsCredenciales.por_departamento.filter(
                (item) =>
                  item &&
                  item.departamento &&
                  item.departamento.trim() !== '' &&
                  item.cantidad > 0
              ).length === 0) && (
              <p className="text-blue-200/60 text-center py-4">
                No hay datos disponibles
              </p>
            )}
          </div>
        </div>

        {/* Distribución por Tipo de Empleado (PieChart) */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 text-center">
            Distribución por Tipo de Empleado
          </h3>
          <div className="space-y-3">
            {statsCredenciales?.por_tipo_empleado &&
            statsCredenciales.por_tipo_empleado.length > 0 ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={statsCredenciales.por_tipo_empleado}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="cantidad"
                    nameKey="tipo"
                  >
                    {statsCredenciales.por_tipo_empleado.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                    itemStyle={{ color: '#fff' }}
                    formatter={(value) => [`${value} credenciales`, 'Cantidad']}
                  />
                  <Legend
                    wrapperStyle={{ color: '#fff' }}
                    formatter={(value) => (
                      <span style={{ color: '#fff' }}>{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-blue-200/60 text-center py-4">
                No hay datos disponibles
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Distribución por Acceso - AHORA ES UN GRÁFICO DE BARRAS HORIZONTAL */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 text-center">
            Distribución por Acceso
          </h3>
          {accesosData.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart
                data={accesosData}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#ffffff20" />
                <XAxis type="number" tick={{ fill: '#fff' }} />
                <YAxis
                  type="category"
                  dataKey="acceso_corto"
                  tick={{ fill: '#fff', fontSize: 11 }}
                  width={100}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(0,0,0,0.8)',
                    border: 'none',
                    borderRadius: '8px',
                  }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value, name, props) => {
                    const originalName = accesosData.find(
                      (item) => item.acceso_corto === props.payload.acceso_corto
                    )?.acceso;
                    return [`${value} credenciales`, originalName || name];
                  }}
                />
                <Bar dataKey="cantidad" fill="#8B5CF6" radius={[0, 4, 4, 0]}>
                  {accesosData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-blue-200/60 text-center py-4">
              No hay datos disponibles
            </p>
          )}
        </div>

        {/* Distribución por Departamentos - AHORA ES UN PIE CHART */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4 text-center">
            Distribución por Departamentos
          </h3>
          <div className="flex justify-center">
            {departamentosStats && departamentosStats.length > 0 ? (
              <ResponsiveContainer width="100%" height={400}>
                <PieChart margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <Pie
                    data={departamentosStats.slice(0, 12)}
                    innerRadius="60%"
                    outerRadius="90%"
                    cornerRadius="5%"
                    paddingAngle={5}
                    dataKey="cantidad"
                    nameKey="departamento"
                    isAnimationActive={true}
                    labelLine={{ stroke: '#ffffff60', strokeWidth: 1 }}
                  >
                    {departamentosStats.slice(0, 12).map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                        stroke="rgba(255,255,255,0.2)"
                        strokeWidth={1}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'rgba(0,0,0,0.8)',
                      border: 'none',
                      borderRadius: '8px',
                    }}
                    itemStyle={{ color: '#fff' }}
                    formatter={(value, name, props) => {
                      const originalName = accesosData.find(
                        (item) =>
                          item.acceso_corto === props.payload.acceso_corto
                      )?.acceso;
                      return [`${value} credenciales`, originalName || name];
                    }}
                  />
                  <Legend
                    wrapperStyle={{ color: '#fff', fontSize: '11px' }}
                    formatter={(value) => (
                      <span style={{ color: '#fff' }}>{value}</span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-blue-200/60 text-center py-4">
                No hay datos disponibles
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Últimos accesos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4">
            Últimos Accesos
          </h3>
          <div className="space-y-3">
            {statsAdmin?.ultimos_accesos?.slice(0, 5).map((acceso, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between py-2 border-b border-white/10 last:border-0"
              >
                <div className="flex-1">
                  <p className="font-medium text-white text-sm">
                    {acceso.username}
                  </p>
                  <p className="text-xs text-blue-200/60">
                    {new Date(acceso.fecha_intento).toLocaleString()}
                  </p>
                </div>
                <div>
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      acceso.fue_exitoso
                        ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                        : 'bg-red-500/20 text-red-300 border border-red-500/30'
                    }`}
                  >
                    {acceso.fue_exitoso ? 'Éxito' : 'Fallido'}
                  </span>
                </div>
              </div>
            ))}
            {(!statsAdmin?.ultimos_accesos ||
              statsAdmin.ultimos_accesos.length === 0) && (
              <p className="text-blue-200/60 text-center py-4">
                No hay registros recientes
              </p>
            )}
          </div>
        </div>

        {/* Administradores */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
          <h3 className="text-lg font-semibold text-white mb-4">
            Administradores
          </h3>
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="text-3xl font-bold text-white">
                {statsAdmin?.total_administradores || 0}
              </p>
              <p className="text-sm text-blue-200">Total administradores</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-green-400">
                {statsAdmin?.administradores_activos || 0}
              </p>
              <p className="text-sm text-blue-200">Activos</p>
            </div>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-green-400 to-green-500 h-2 rounded-full transition-all duration-500"
              style={{
                width: `${((statsAdmin?.administradores_activos || 0) / (statsAdmin?.total_administradores || 1)) * 100}%`,
              }}
            ></div>
          </div>
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="flex justify-between text-sm">
              <span className="text-blue-200">Logs exitosos hoy:</span>
              <span className="font-medium text-green-400">
                {statsAdmin?.logs_exitosos_hoy || 0}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Últimas credenciales agregadas */}
      <div className="bg-white/10 backdrop-blur-xl rounded-2xl shadow-xl p-6 border border-white/20">
        <h3 className="text-lg font-semibold text-white mb-4">
          Últimas Credenciales Agregadas
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-white/20">
            <thead className="bg-white/5">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-blue-200 uppercase">
                  Nombre
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-blue-200 uppercase">
                  Departamento
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-blue-200 uppercase">
                  Tipo
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-blue-200 uppercase">
                  Fecha
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {statsCredenciales?.ultimos_agregados?.map((cred) => (
                <tr
                  key={cred.id}
                  className="hover:bg-white/5 transition-colors"
                >
                  <td className="px-4 py-3 text-sm text-white">
                    {cred.nombres} {cred.apellido_paterno}{' '}
                    {cred.apellido_materno}
                  </td>
                  <td className="px-4 py-3 text-sm text-blue-200">
                    {cred.departamento || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-blue-200">
                    {cred.tipo_empleado || '-'}
                  </td>
                  <td className="px-4 py-3 text-sm text-blue-200/60">
                    {cred.created_at
                      ? new Date(cred.created_at).toLocaleDateString()
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Estadisticas;
